import { CommonModule } from "@angular/common";
import { Component, inject, input, OnInit, output, ViewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { DeviceApi } from "@ramudden/data-access/resource/device.api";
import { PlannedAssignmentApi } from "@ramudden/data-access/resource/planned-assignment.api";
import { PlannedVehiclesApi } from "@ramudden/data-access/resource/planned-vehicles.api";
import { DeviceType } from "@ramudden/models/device";
import { IPlannedAssignmentCustomVehicle } from "@ramudden/models/planned-event";
import { FilterDescriptor, FilterOperator, SearchParameters, ServiceRequestOptions } from "@ramudden/models/search";
import { ModalComponent } from "@ramudden/ui";
import moment from "moment/moment";
import { debounceTime, distinctUntilChanged, firstValueFrom, Observable, of, startWith, switchMap } from "rxjs";
import { LoaderIconComponent } from "../../../../components/loader-icon/loader-icon.component";
import { VehicleComponent } from "../../../../components/vehicle/vehicle.component";
import { AppService } from "../../../../services/app.service";
import { LoaderService } from "../../../../services/loader.service";

type Vehicle = {
    deviceId: number;
    deviceCode: string;
    takenBy?: string;
    isWJet?: boolean;
};

export type ISelectedVehicle = {
    deviceId: number;
    deviceCode: string;
    isWJet: boolean;
    isActive: boolean;
    isDisabled: boolean;
    takenBy: string;
    isTaken: boolean;
};

@Component({
    selector: "app-vehicle-dialog",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ModalComponent,
        VehicleComponent,
        TranslateModule,
        FormsModule,
        LoaderIconComponent,
    ],
    templateUrl: "./vehicle-dialog.component.html",
    styleUrls: ["./vehicle-dialog.component.scss"],
})
export class VehicleDialogComponent implements OnInit {
    allowClose = input<boolean>(false);
    usedInSetup = input<boolean>(true);
    customVehicles: IPlannedAssignmentCustomVehicle[];

    @ViewChild(ModalComponent, { static: true }) dialog: ModalComponent;

    nextClicked = output<ISelectedVehicle[]>();
    previousClicked = output<void>();

    defaultVehicles: ISelectedVehicle[] = [];
    otherVehicles: ISelectedVehicle[] = [];
    wJetVehicles: ISelectedVehicle[] = [];

    private readonly appService = inject(AppService);
    private readonly deviceApi = inject(DeviceApi);
    private readonly loaderService = inject(LoaderService);
    private readonly plannedAssignmentApi = inject(PlannedAssignmentApi);
    private readonly plannedVehicleApi = inject(PlannedVehiclesApi);

    searchControl = new FormControl("");
    filteredVehicles$: Observable<ISelectedVehicle[]>;

    isLoading: Observable<boolean>;

    ngOnInit() {
        this.isLoading = this.loaderService.loading$;
    }

    async openModal(customVehicles?: IPlannedAssignmentCustomVehicle[]) {
        this.customVehicles = customVehicles;
        this.dialog.openModal();

        const eventDate = this.appService.plannedEvent.eventDate;
        const isNightShift = this.appService.plannedEvent.isNightShift;
        const idsOfTeamMembers: number[] = this.appService.selectedWorkers.map((x) => x.id);
        const plannedEventId = this.appService.plannedEvent.id;
        const plannedAssignmentId: number = null;

        await this.getVehicles(eventDate, isNightShift, idsOfTeamMembers, plannedEventId, plannedAssignmentId);

        this.filteredVehicles$ = this.searchControl.valueChanges.pipe(
            startWith(""),
            debounceTime(50),
            distinctUntilChanged(),
            switchMap((searchText) => this.filterVehicles(searchText)),
        );
    }

    filterVehicles(searchText: string): Observable<ISelectedVehicle[]> {
        const filterValue = searchText.toLowerCase();
        return of(this.otherVehicles.filter((vehicle) => vehicle.deviceCode.toLowerCase().includes(filterValue)));
    }

    onPreviousClicked() {
        this.dialog.closeModal();
        this.previousClicked.emit();
    }

    closeModal() {
        const selectedVehicles = [
            ...this.defaultVehicles.filter((vehicle) => vehicle.isActive),
            ...this.otherVehicles.filter((vehicle) => vehicle.isActive),
            ...this.wJetVehicles.filter((vehicle) => vehicle.isActive),
        ];

        if (selectedVehicles.length === 0) {
            alert("Please select at least one vehicle.");
            return;
        }

        this.dialog.closeModal();
        this.nextClicked.emit(selectedVehicles);
    }

    protected convertVehicleToSelectedVehicle(vehicle: Vehicle): ISelectedVehicle {
        let isActive = this.appService.plannedEvent.plannedVehicles.some((pv) => pv.deviceId === vehicle.deviceId);
        if (this.customVehicles?.length > 0) {
            isActive = this.customVehicles.some((cv) => cv.deviceId === vehicle.deviceId);
        }
        const isTaken = !!vehicle.takenBy;

        return {
            deviceId: vehicle.deviceId,
            deviceCode: vehicle.deviceCode,
            isWJet: vehicle.isWJet,
            isActive: isActive,
            isTaken: isTaken,
            takenBy: isTaken ? vehicle.takenBy : "",
            isDisabled: isTaken,
        };
    }

    protected onVehicleSelected(vehicle: ISelectedVehicle) {
        if (this.defaultVehicles.some((defaultVehicle) => defaultVehicle.deviceId === vehicle.deviceId)) {
            this.defaultVehicles.find((defaultVehicle) => defaultVehicle.deviceId === vehicle.deviceId).isActive =
                !vehicle.isActive;
        } else if (this.otherVehicles.some((otherVehicle) => otherVehicle.deviceId === vehicle.deviceId)) {
            this.otherVehicles.find((otherVehicle) => otherVehicle.deviceId === vehicle.deviceId).isActive =
                !vehicle.isActive;
        } else if (this.wJetVehicles.some((wJetVehicle) => wJetVehicle.deviceId === vehicle.deviceId)) {
            this.wJetVehicles.find((wJetVehicle) => wJetVehicle.deviceId === vehicle.deviceId).isActive =
                !vehicle.isActive;
        }
    }

    //region Get Vehicles
    async getVehicles(
        eventDate: Date,
        isNightShift: boolean,
        idsOfTeamMembers: number[],
        plannedEventId: number,
        plannedAssignmentId?: number,
    ) {
        const takenVehicleServiceRequestOptions = new ServiceRequestOptions();
        takenVehicleServiceRequestOptions.includes.add("PlannedVehicle", "PlannedEvent");
        takenVehicleServiceRequestOptions.includes.add("PlannedVehicle", "Device");
        takenVehicleServiceRequestOptions.includes.add("PlannedEvent", "PlannedWorkers");
        takenVehicleServiceRequestOptions.includes.add("PlannedWorker", "Worker");

        const searchParametersNotAvailable = new SearchParameters();
        searchParametersNotAvailable.filter = [];
        searchParametersNotAvailable.filter.push(
            new FilterDescriptor("EventDate", moment(eventDate).format("YYYY/MM/DD")),
        );
        searchParametersNotAvailable.filter.push(new FilterDescriptor("IsNightShift", isNightShift));

        this.loaderService.show();
        const allTakenVehicles = await firstValueFrom(
            this.plannedVehicleApi.getAll$(searchParametersNotAvailable, null, takenVehicleServiceRequestOptions),
        ).finally(() => this.loaderService.hide());
        const allTakenVehiclesByTeam = allTakenVehicles
            .filter((x) => x.plannedEventId === plannedEventId)
            .map((x) => x.device);

        if (plannedAssignmentId !== null) {
            const customVehiclesServiceRequestOptions = new ServiceRequestOptions();
            customVehiclesServiceRequestOptions.includes.add("PlannedAssignment", "CustomVehicles");
            customVehiclesServiceRequestOptions.includes.add("PlannedAssignmentCustomVehicle", "Device");

            this.loaderService.show();
            const plannedAssignment = await firstValueFrom(
                this.plannedAssignmentApi.get$(plannedAssignmentId, null, customVehiclesServiceRequestOptions),
            ).finally(() => this.loaderService.hide());
            if (plannedAssignment.customVehicles.length > 0) {
                allTakenVehiclesByTeam.push(...plannedAssignment.customVehicles.map((x) => x.device));
            }
        }

        const allVehiclesSearchParameters = new SearchParameters();
        allVehiclesSearchParameters.filter = [];
        allVehiclesSearchParameters.filter.push(
            new FilterDescriptor(
                "TypeId",
                `${DeviceType.Vehicle.toString()}|${DeviceType.WJet.toString()}`,
                FilterOperator.in,
            ),
        );
        allVehiclesSearchParameters.queryParams = { organizationId: this.appService.selectedWorker.organizationId };

        const serviceRequestOptions = new ServiceRequestOptions();
        serviceRequestOptions.includes.add("Device", "WorkerDriver");

        this.loaderService.show();
        const allVehicles = await firstValueFrom(
            this.deviceApi.getAll$(allVehiclesSearchParameters, null, serviceRequestOptions),
        ).finally(() => this.loaderService.hide());
        const wJetDevices = allVehicles.filter((x) => x.typeId === DeviceType.WJet);

        const vehicleDevices = allVehicles.filter((x) => x.typeId === DeviceType.Vehicle);
        const vehiclesForWhichTeamMembersAreDrivers = vehicleDevices.filter(
            (x) => x.workerDriver !== undefined && idsOfTeamMembers.contains(x.workerDriver?.id),
        );

        let remainingVehicles = vehicleDevices.filter(
            (d) =>
                allTakenVehiclesByTeam.some((x) => x.id === d.id) || !allTakenVehicles.some((v) => v.deviceId === d.id),
        );
        remainingVehicles = remainingVehicles.filter(
            (d) => !vehiclesForWhichTeamMembersAreDrivers.some((v) => v.id === d.id),
        );

        const teamVehicles: Vehicle[] = [];
        vehiclesForWhichTeamMembersAreDrivers.forEach((teamVehicle) => {
            const vehicle: Vehicle = {
                deviceId: teamVehicle.id,
                deviceCode: teamVehicle.code,
                isWJet: false,
            };
            const isTaken =
                allTakenVehicles.some((x) => x.deviceId === teamVehicle.id) &&
                !allTakenVehiclesByTeam.some((x) => x.id === teamVehicle.id);
            if (isTaken) {
                const plannedVehicle = allTakenVehicles.find((x) => x.deviceId === teamVehicle.id);

                if (plannedVehicle) {
                    const teamLeader = plannedVehicle.plannedEvent.plannedWorkers.sort(
                        (a, b) => a.displayOrder - b.displayOrder,
                    )[0].worker;
                    vehicle.takenBy = `${teamLeader.firstName} ${teamLeader.lastName}`;
                }
            }

            teamVehicles.push(vehicle);
        });

        const notTakenVehicles = remainingVehicles.map((remainVehicle) => {
            return {
                deviceId: remainVehicle.id,
                deviceCode: remainVehicle.code,
                isWJet: false,
            } as Vehicle;
        });

        const wJetVehicles: Vehicle[] = [];
        wJetDevices.forEach((wJetDevice) => {
            const vehicle: Vehicle = {
                deviceId: wJetDevice.id,
                deviceCode: wJetDevice.code,
                isWJet: true,
            };
            const isTaken =
                allTakenVehicles.some((x) => x.deviceId === wJetDevice.id) &&
                !allTakenVehiclesByTeam.some((x) => x.id === wJetDevice.id);

            if (isTaken) {
                const plannedVehicle = allTakenVehicles.find((x) => x.deviceId === wJetDevice.id);

                if (plannedVehicle !== null) {
                    const teamLeader = plannedVehicle.plannedEvent.plannedWorkers.sort(
                        (a, b) => a.displayOrder - b.displayOrder,
                    )[0].worker;
                    vehicle.takenBy = `${teamLeader.firstName} ${teamLeader.lastName}`;
                }
            }

            wJetVehicles.push(vehicle);
        });

        this.defaultVehicles = teamVehicles.map((x) => this.convertVehicleToSelectedVehicle(x));
        this.otherVehicles = notTakenVehicles.map((x) => this.convertVehicleToSelectedVehicle(x));
        this.wJetVehicles = wJetVehicles.map((x) => this.convertVehicleToSelectedVehicle(x));
    }

    //endregion
}
